import { Accordion } from './components/Accordion';
import { BulletPointsWithMedia } from './components/BulletPointsWithMedia/BulletPointsWithMedia';
import { BusinessChallenge } from './components/BusinessChallenge';
import { Introduction } from './components/Introduction';
import { MetricCards } from './components/MetricCards';
import { Solutions } from './components/Problem';
import { SectionIntroductionText } from './components/SectionIntroductionText';
import { ProductCards } from './components/ProductCards';
import { TextWithIconCards } from './components/TextWithIconCards';
import { TextWithMedia } from './components/TextWithMedia/TextWithMedia';
import { TitleBanner } from './components/TitleBanner';
import { IconName } from './components/ui/Icon/Icon';
import { WorkflowDiagram } from './components/WorkflowDiagram';
import { TitledSection } from './layout';
import { DashedTitleSection } from './layout/DashedTitleSection';
import { MediaConfigWithSide } from './types';

export type CaseStudyUIGeneratorConfig =
  | {
      type: 'titleBanner';
      properties: {
        caseStudyTitle: string;
        description: string;
        bannerImageUrl: string;
        companyLogoUrl: string;
        projectName: string;
      };
    }
  | {
      type: 'introduction';
      properties: {
        title: string;
        paragraphs: string[];
        tagGroups: { heading: string; items: string[] }[];
        mediaUrl: string;
      };
    }
  | {
      type: 'businessChallenge';
      properties: {
        text: string;
        highlightedPhrases: string[];
      };
    }
  | {
      type: 'titledSection';
      title: string;
      contents: CaseStudyUIGeneratorConfig[];
    }
  | {
      type: 'dashedTitleSection';
      properties: {
        title: string;
        contents: CaseStudyUIGeneratorConfig[];
      };
    }
  | {
      type: 'metricCards';
      properties: {
        metrics: {
          iconName: IconName;
          label: string;
          value: string;
        }[];
      };
    }
  | { type: 'introductionText'; properties: { text: string } }
  | {
      type: 'solutions';
      properties: { solutions: { heading: string; problemText: string; solutionText: string }[] };
    }
  | {
      type: 'productCards';
      properties: {
        products: { title: string; subtitle: string; description: string; mediaSrc: string }[];
      };
    }
  | {
      type: 'accordion';
      properties: {
        items: {
          title: string;
          substitle: string;
          description: string;
          accordionContent: CaseStudyUIGeneratorConfig[];
        }[];
      };
    }
  | {
      type: 'bulletPoints';
      properties: {
        bulletPoints: string[];
        resultText: string;
        media: MediaConfigWithSide;
      };
    }
  | {
      type: 'textWithMedia';
      properties: {
        paragraphs: string[];
        media: MediaConfigWithSide;
      };
    }
  | {
      type: 'workflowDiagram';
      properties: {
        steps: (
          | {
              type: 'entity';
              title: string;
              subtitle: string;
              iconName: IconName;
            }
          | {
              type: 'connection';
              title: string;
            }
        )[];
      };
    }
  | {
      type: 'textWithIconCards';
      properties: {
        heading: string;
        cards: {
          heading: string;
          text: string;
          iconName: IconName;
        }[];
      };
    };

export const CaseStudyUIGenerator = (props: CaseStudyUIGeneratorConfig) => {
  const { type } = props;
  switch (type) {
    case 'titleBanner':
      return (
        <TitleBanner
          bannerSrc={props.properties.bannerImageUrl}
          bannerTitle={props.properties.caseStudyTitle}
          bannerSubtitle={props.properties.description}
          logoSrc={props.properties.companyLogoUrl}
          projectName={props.properties.projectName}
        />
      );
    case 'introduction':
      return (
        <Introduction
          title={props.properties.title}
          paragraphs={props.properties.paragraphs}
          tags={props.properties.tagGroups}
          mediaUrl={props.properties.mediaUrl}
        />
      );
    case 'businessChallenge':
      return (
        <BusinessChallenge text={props.properties.text} highlightedPhrases={props.properties.highlightedPhrases} />
      );
    case 'titledSection':
      return (
        <TitledSection title={props.title}>
          {props.contents.map((config, index) => (
            <CaseStudyUIGenerator key={index} {...config} />
          ))}
        </TitledSection>
      );
    case 'dashedTitleSection':
      return (
        <DashedTitleSection title={props.properties.title}>
          {props.properties.contents.map((config, index) => (
            <CaseStudyUIGenerator key={index} {...config} />
          ))}
        </DashedTitleSection>
      );
    case 'metricCards':
      return <MetricCards metrics={props.properties.metrics} />;
    case 'introductionText':
      return <SectionIntroductionText text={props.properties.text} />;
    case 'solutions':
      return <Solutions solutions={props.properties.solutions} />;
    case 'productCards':
      return <ProductCards products={props.properties.products} />;
    case 'accordion':
      return (
        <Accordion
          items={props.properties.items.map(item => ({
            title: item.title,
            subtitle: item.substitle,
            description: item.description,
            content: (
              <>
                {item.accordionContent.map((contentConfig, index) => (
                  <CaseStudyUIGenerator key={index} {...contentConfig} />
                ))}
              </>
            ),
          }))}
        />
      );
    case 'textWithMedia':
      return <TextWithMedia paragraphs={props.properties.paragraphs} media={props.properties.media} />;
    case 'bulletPoints':
      return (
        <BulletPointsWithMedia
          bulletPoints={props.properties.bulletPoints}
          resultText={props.properties.resultText}
          media={props.properties.media}
        />
      );
    case 'workflowDiagram':
      return <WorkflowDiagram steps={props.properties.steps} />;
    case 'textWithIconCards':
      return <TextWithIconCards heading={props.properties.heading} cards={props.properties.cards} />;
  }
};
